/**
 * React Query Configuration - Subtask 4: React Query Setup
 * Global QueryClient configuration with defaults and error handling
 */

import { QueryClient, QueryClientConfig } from '@tanstack/react-query';

/**
 * Default query options
 */
/**
 * Read the HTTP status off an error thrown by the app's API layers.
 * src/api/http.ts attaches `status` to every normalized Error; raw axios
 * errors carry `response.status`. Message-substring sniffing is a last
 * resort only — the transport layer rewrites messages, so codes rarely
 * appear in them.
 */
const errorHttpStatus = (error: unknown): number | undefined => {
  const e = error as { status?: unknown; response?: { status?: unknown } };
  if (typeof e?.status === 'number') return e.status;
  if (typeof e?.response?.status === 'number') return e.response.status;
  return undefined;
};

const defaultQueryOptions = {
  staleTime: 1000 * 60 * 5, // 5 minutes
  cacheTime: 1000 * 60 * 10, // 10 minutes (v4 name; renamed to gcTime in v5)
  retry: (failureCount: number, error: unknown) => {
    // Don't retry on 4xx errors (except 408, 429)
    const status = errorHttpStatus(error);
    if (
      status !== undefined &&
      status >= 400 &&
      status < 500 &&
      status !== 408 &&
      status !== 429
    ) {
      return false;
    }
    if (status === undefined && error instanceof Error) {
      // Fallback for errors that lost their status (e.g. plain fetch paths).
      const message = error.message;
      if (
        message.includes('401') ||
        message.includes('403') ||
        message.includes('404')
      ) {
        return false;
      }
    }
    // Retry up to 2 times for other errors. Kept close to the app's previous
    // global `retry: 1` so errors still surface promptly in the UI; the win
    // over the old config is skipping retries entirely on 4xx.
    return failureCount < 2;
  },
  retryDelay: (attemptIndex: number) => {
    // Exponential backoff: 1s, 2s, 4s
    return Math.min(1000 * 2 ** attemptIndex, 30000);
  },
  // Deliberately false: the app has always shipped with focus-refetch off
  // (previously set in App.tsx). Turning it on app-wide would be a surprising
  // behavior change; opt in per-query where freshness on focus matters.
  refetchOnWindowFocus: false,
  refetchOnReconnect: true,
  refetchOnMount: true,
};

/**
 * Default mutation options
 */
const defaultMutationOptions = {
  // Mutations are not retried automatically (react-query's own default).
  // A timed-out or connection-dropped POST may well have reached the server,
  // so a blind retry can create duplicate resources; the old `includes('4')`
  // message sniff here also misfired on any digit 4. Opt in per-mutation
  // where the operation is known to be idempotent.
  retry: 0,
};

/**
 * Create and configure QueryClient
 */
export const createQueryClient = (): QueryClient => {
  return new QueryClient({
    defaultOptions: {
      queries: defaultQueryOptions,
      mutations: defaultMutationOptions,
    },
  } as QueryClientConfig);
};

/**
 * Global error handler for all queries and mutations
 */
export const handleQueryError = (error: unknown) => {
  if (error instanceof Error) {
    const message = error.message;

    // Handle authentication errors
    if (message.includes('401') || message.includes('Unauthorized')) {
      // Redirect to login or refresh token
      console.error('Authentication error:', message);
      // This would typically trigger a redirect to login
      return;
    }

    // Handle authorization errors
    if (message.includes('403') || message.includes('Forbidden')) {
      console.error('Authorization error:', message);
      return;
    }

    // Handle not found errors
    if (message.includes('404') || message.includes('Not Found')) {
      console.error('Resource not found:', message);
      return;
    }

    // Handle server errors
    if (message.includes('500') || message.includes('Server Error')) {
      console.error('Server error:', message);
      return;
    }

    // Handle network errors
    if (message.includes('Network') || message.includes('Failed to fetch')) {
      console.error('Network error:', message);
      return;
    }

    console.error('Query error:', message);
  }
};

/**
 * Query client instance
 */
export const queryClient = createQueryClient();

/**
 * Invalidate all queries
 */
export const invalidateAllQueries = () => {
  return queryClient.invalidateQueries();
};

/**
 * Invalidate queries by key prefix
 */
export const invalidateQueriesByPrefix = (prefix: string) => {
  return queryClient.invalidateQueries({
    queryKey: [prefix],
  });
};

/**
 * Clear all queries
 */
export const clearAllQueries = () => {
  queryClient.clear();
};

/**
 * Get query data
 */
export const getQueryData = <T>(queryKey: unknown[]): T | undefined => {
  return queryClient.getQueryData(queryKey);
};

/**
 * Set query data
 */
export const setQueryData = <T>(queryKey: unknown[], data: T) => {
  queryClient.setQueryData(queryKey, data);
};

/**
 * Remove query
 */
export const removeQuery = (queryKey: unknown[]) => {
  queryClient.removeQueries({ queryKey });
};

export default queryClient;
